import { PROPERTY_GRID_SUBJECT_PROPERTY } from '@aloft/toolkit-shared';
import { Grid } from '@aloft/toolkit-sui';

import React, { useEffect, useMemo, useRef } from 'react';
import { FormProvider } from 'react-hook-form';

import { useZodForm } from '../../hooks/useZodForm.ts';
import { selectCompSequence, selectOrder } from '../../redux/features/selectors/orderSelectors.ts';
import { selectFeatureGridExpandedPropertyId } from '../../redux/features/selectors/uiSelectors.ts';
import { useAppSelector } from '../../redux/hooks.ts';
import { PropertyGridColumn } from './PropertyGridColumn.tsx';
import { FEATURE_GRID_DATA_SOURCES } from './consts.ts';
import {
  ColumnType,
  PropertyGridColumnSectionComponent,
  PropertyGridRowFieldType,
  PropertyGridType,
  schPropertyGridForm,
} from './types.ts';
import { getDefaultFormColumns } from './utils.ts';

type PropertyGridProps = {
  columnSections: PropertyGridColumnSectionComponent[];
  gridType: PropertyGridType;
};

export const PropertyGrid = ({ columnSections, gridType }: PropertyGridProps) => {
  const compSequence = useAppSelector(selectCompSequence);
  const featureGridExpandedPropertyId = useAppSelector(selectFeatureGridExpandedPropertyId);
  const prevCompSequenceRef = useRef<string[]>(compSequence);
  const { hasArvOption } = useAppSelector(selectOrder);

  const isFeatureGrid = gridType === 'FeatureGrid';

  const zodForm = useZodForm({
    schema: schPropertyGridForm,
    defaultValues: schPropertyGridForm.parse({
      columns: getDefaultFormColumns(compSequence, FEATURE_GRID_DATA_SOURCES),
      dataSources: FEATURE_GRID_DATA_SOURCES,
    }),
    mode: 'onChange',
  });

  const { watch, setValue } = zodForm;

  const columns = watch('columns');

  // Update form columns only when new comps are added (not when sequence changes)
  useEffect(() => {
    const prevCompSequence = prevCompSequenceRef.current;
    const newComps = compSequence.filter((compId) => !prevCompSequence.includes(compId));

    if (newComps.length > 0) {
      // Only add new columns, don't replace existing ones
      const currentColumns = columns || [];
      const newColumns = getDefaultFormColumns(compSequence, FEATURE_GRID_DATA_SOURCES);

      // Find columns that don't exist yet and add them
      const columnsToAdd = newColumns.filter(
        (newCol) =>
          !currentColumns.some(
            (existingCol) =>
              existingCol.propertyId === newCol.propertyId && existingCol.type === newCol.type,
          ),
      );

      if (columnsToAdd.length > 0) {
        setValue('columns', [...currentColumns, ...columnsToAdd]);
      }
    }

    prevCompSequenceRef.current = compSequence;
  }, [compSequence, columns, setValue]);

  const orderedColumns = useMemo(() => {
    const subjectCols = columns.filter(
      ({ propertyId }) => propertyId === PROPERTY_GRID_SUBJECT_PROPERTY,
    );

    const compCols = compSequence.flatMap((compId) => {
      const existingCol = columns.filter(({ propertyId }) => propertyId === compId);
      if (existingCol.length > 0) return existingCol;

      return [
        {
          propertyId: compId,
          type: 'property' as ColumnType,
        },
        ...FEATURE_GRID_DATA_SOURCES.map((source) => ({
          propertyId: compId,
          type: source,
        })),
      ];
    });

    return [...subjectCols, ...compCols];
  }, [compSequence, columns]);

  const visibleColumns = orderedColumns.filter(
    ({ type, propertyId }) =>
      type === 'property' || (isFeatureGrid && featureGridExpandedPropertyId === propertyId),
  );

  const isDataSourceDrawerOpen =
    isFeatureGrid &&
    visibleColumns.some(({ propertyId }) => featureGridExpandedPropertyId === propertyId);

  return (
    <FormProvider {...zodForm}>
      <Grid
        gridTemplateColumns={`repeat(${visibleColumns.length}, 350px)`}
        paddingLeft="10px"
        paddingBottom="5rem"
      >
        {orderedColumns.map((col) => {
          const { propertyId, type } = col;
          const isSubjectProperty = propertyId === PROPERTY_GRID_SUBJECT_PROPERTY;
          const propertyIsExpanded = isFeatureGrid && featureGridExpandedPropertyId === propertyId;

          const showFieldLabels = type === 'property' && (isSubjectProperty || propertyIsExpanded);
          const fieldTypes: PropertyGridRowFieldType[] = [];

          if (showFieldLabels) fieldTypes.push('label');
          if (isFeatureGrid) {
            if (type === 'property') {
              fieldTypes.push('editableValue');
            } else {
              fieldTypes.push('dataSourceValue');
            }
          } else if (gridType === 'AdjustmentsGrid') {
            fieldTypes.push('readOnlyValue');
            if (!showFieldLabels) fieldTypes.push('adjustment');
          }
          if (hasArvOption) fieldTypes.push('editableArvValue');

          return (
            <React.Fragment key={`${propertyId}-${type}`}>
              <PropertyGridColumn
                fieldTypes={fieldTypes}
                columnSections={columnSections}
                gridType={gridType}
                isDataSourceDrawerOpen={isDataSourceDrawerOpen}
                propertyId={propertyId}
                type={type}
              />
            </React.Fragment>
          );
        })}
      </Grid>
    </FormProvider>
  );
};
